import React, { useState, useCallback, useMemo, useEffect } from "react";
import classnames from "classnames";
import {
  Grid,
  Button,
  Popover,
  IconButton,
  Typography,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Checkbox,
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import AccountTypeList from "@instream-solutions/instream-design-system/lib/Data/AccountTypeList";
import { connect } from "react-redux";
import AssetCard from "./AssetCard";
import {
  CUSTODIAN_TDAMERITRADE,
  MANAGED_MANAGED,
  RMD_ACCOUNT_TYPES,
} from "../../../../api/advisor/accounts";
import {
  convertYearToAgeByClient,
  displayCurrencyNoDecimals,
  getCurrentMonth,
  getCurrentYear,
  MONTH_LABEL_MAP,
} from "../../../../common/shared";
import { DeleteConfirmModal } from "../../ConfirmModal/ConfirmModal";
import { getDefaultFirstToRetireDate } from "../utils";
import { getClientAppliesTo } from "../../../../common/shared";
import {
  formatNumberToCurrency,
  formatTimestampToDate,
} from "../../../../common/formatter";
import AssetDistributionSort from "./AssetDistributionSort";
import { getDefaultContributionLimits } from "../../../../api/admin/defaultPlanSettings";

const columns = [
  {
    id: "isRMDEnable",
    label: "",
    minWidth: 150,
  },
  {
    id: "accountType",
    label: "Account Type",
    minWidth: 350,
  },
  {
    id: "currentValue",
    label: "Balance Amount",
    minWidth: 350,
  },
];

const AssetsTab = (props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [assets, setAssets] = useState(props.assets ? props.assets : []);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [defaultContributionLimits, setDefaultContributionLimits] = useState(
    []
  );

  const currentUser = props.currentUser.toJS();

  // client details
  const client = useMemo(() => props.currentClient.get("client").toJS(), [
    props.currentClient,
  ]);
  const coClient = useMemo(() => props.currentClient.get("coClient").toJS(), [
    props.currentClient,
  ]);
  const clients = coClient.id !== undefined ? [client, coClient] : [client];

  const accountTypes = AccountTypeList(t);

  useEffect(() => {
    props.updateAssets(assets);
    getDefaultContributionLimits(currentUser.advisor.advisorId).then(
      (response) => {
        setDefaultContributionLimits(response);
      }
    );
  }, [assets]);

  const getAge = (owner, clientInformation) => {
    let selectedClient;
    if (clientInformation.client.id === owner.id) {
      selectedClient = clientInformation.client;
    }
    if (clientInformation.coClient.id === owner.id) {
      selectedClient = clientInformation.coClient;
    }

    return selectedClient
      ? convertYearToAgeByClient(
          getCurrentYear(),
          selectedClient,
          MONTH_LABEL_MAP[getCurrentMonth() + 1]
        )
      : 0;
  };

  const getContributionLimit = (model) => {
    const clientAge = getAge(model.owner, {
      client: client,
      coClient: coClient,
    });
    let fittedAge = clientAge > 50 ? 51 : null;
    let fittedItems = defaultContributionLimits.filter(
      (item) =>
        item.accountType == model.accountType.key && item.age == fittedAge
    );
    if (fittedItems.length === 0) {
      fittedItems = defaultContributionLimits.filter(
        (item) => item.accountType == model.accountType.key && item.age == null
      );
    }
    return fittedItems.length > 0
      ? formatNumberToCurrency(fittedItems[0].contributionLimit, 0)
      : null;
  };

  const updateDefaultDates = (asset, ownerId) => {
    const firstToRetireDate = getDefaultFirstToRetireDate(
      ownerId,
      client,
      coClient
    );

    return {
      ...asset,
      endMonth: firstToRetireDate.getMonth() + 1,
      endYear: firstToRetireDate.getFullYear(),
    };
  };

  const generateAsset = useCallback(
    (asset, editIndex) => {
      return {
        id: null,
        name: "",
        isReinvest: false,
        editIndex: editIndex,
        sortOrder: editIndex + 1,
        contributionId: null,
        allocationPercentage: 0.0,
        preRetirementAllocation: 0.0,
        postRetirementAllocation: 0.0,
        frequencyType: "MONTHLY",
        taxCategory: {
          key: asset.category,
          name: asset.category,
        },
        accountType: {
          key: asset.key,
          name: asset.name,
          category: asset.category,
        },
        owner: getClientAppliesTo(),
        contributionType: "monthly",
        monthlyContribution: 0,
        percentContribution: 0,
        currentValue: 0,
        costBasis: 0,
        valueAsOfDate: formatTimestampToDate(
          new Date().getTime(),
          "MM/dd/yyyy"
        ),
        custodian: {
          key: CUSTODIAN_TDAMERITRADE,
          name: t("ClientDetails.Profile.Accounts.Custodian.tdameritrade"),
        },
        custodianName: "",
        accountNumber: "",
        managed: {
          key: MANAGED_MANAGED,
          name: t("ClientDetails.Profile.Accounts.Managed.managed"),
        },
        startYear: new Date().getFullYear(),
        startMonth: new Date().getMonth() + 1,
        endYear: new Date().getFullYear(),
        endMonth: 1,
        managedBy: "",
        clientUserName: "",
        clientPassword: "",
        usedInPlans: [],
        contributions: [],
        withdrawals: [],
        transfers: [],
        allocation: null,
        notes: "",
        tabIndex: 0,
        scopeType: "PLAN",
        inEdit: true,
        isExpanded: true,
        isNew: true,
      };
    },
    [t]
  );

  const getAccountByKey = useCallback(
    (key) => {
      return accountTypes.find(({ key: k }) => k === key) || {};
    },
    [accountTypes]
  );

  const handleShowAssetSelector = (e) => {
    setAnchorEl(e.currentTarget);
  };
  const handleHideAssetSelector = () => {
    setAnchorEl(null);
  };

  const handleAddAsset = (assetType) => () => {
    const asset = getAccountByKey(assetType);
    const editIndex =
      assets.length > 0 ? Math.max(...assets.map((e) => e.editIndex)) + 1 : 0;

    setAssets((v) =>
      [...v, generateAsset(asset, editIndex)].map((ast, index) => {
        ast.sortOrder = index + 1;
        return updateDefaultDates(ast, ast.owner.id);
      })
    );
    props.changed();
    setAnchorEl(null);
  };

  const finishRemoveAsset = () => {
    props.deleteItem(assetToDelete).then(() => {
      setAssets((v) =>
        v
          .filter((ast) => ast.editIndex !== assetToDelete.editIndex)
          .map((ast, index) => {
            ast.sortOrder = index + 1;
            return ast;
          })
      );
      setAssetToDelete(null);
      props.changed();
    });
  };

  const handleRemoveAsset = (asset) => () => {
    setAssetToDelete(asset);
  };

  const openAssetSelector = Boolean(anchorEl);
  const assetSelectorId = openAssetSelector ? "asset-selector" : undefined;

  const handleChange = (dataItem) => (key) => (e) => {
    setAssets((v) =>
      v.map((item) => {
        if (item.editIndex !== dataItem.editIndex) return item;

        if (key === "ownerId") {
          return updateDefaultDates(
            {
              ...item,
              owner: clients.find((c) => c.id === e),
              isEdited: true,
            },
            e
          );
        }

        if (
          key === "preRetirementAllocation" ||
          key === "postRetirementAllocation" ||
          key === "currentValue" ||
          key === "monthlyContribution" ||
          key === "percentContribution"
        ) {
          return {
            ...item,
            [key]: e?.target?.value,
            isEdited: true,
          };
        }

        return {
          ...item,
          [key]: e?.target?.value,
          isEdited: true,
        };
      })
    );
    props.changed();
  };

  const hasAsset = (key) => {
    const result = assets.find((item) => {
      return item.accountType.key === key;
    });
    return result !== undefined;
  };

  let content;
  if (props.hasRMD) {
    content = (
      <Grid item md={12}>
        <Grid item>
          <Typography component="h5">
            Required Minimum Distribution (RMD)
          </Typography>
          <Typography component={"label"}>
            Plan Wizard has found that the Plan account type(s) are eligible for
            IRS mandates minimum annual withdrawal called Required Minimum
            Distribution (RMD) starting at age 70 1/2 or 72, depending on the
            year Client was born. The RMD rules apply to all employer sponsored
            retirement plans, such as: profit-sharing plans, 401(k) plans,
            403(b) plans, and 457(b) plans. The RMD rules apply to traditional
            IRAs and IRA-based plans such as SEPs, SARSEPs, and SIMPLE IRAs.
          </Typography>
          <Typography component={"label"}>
            The RMD rules also apply to Roth 401(k) accounts; however, the RMD
            rules do not apply to Roth IRAs while the owner is living.
          </Typography>
        </Grid>
        <Grid item>
          <Card className="asset-rmd-card">
            <Typography className="highlighted-text" component="h6">
              Please select account(s) that the client will default RMD.
            </Typography>
            <Grid
              container
              spacing={4}
              justifyContent="space-between"
              className="monthly-expenses-inputs"
            >
              <Grid item>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{ minWidth: column.minWidth }}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assets
                        .filter(
                          (asset) =>
                            RMD_ACCOUNT_TYPES.indexOf(asset.accountType.key) >
                            -1
                        )
                        .map((asset, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Grid item>
                                <Checkbox
                                  color="primary"
                                  checked={asset.isRMDEnable}
                                  onChange={(e) => {
                                    setAssets((v) =>
                                      v.map((item) => {
                                        if (item.editIndex !== asset.editIndex)
                                          return item;
                                        return {
                                          ...item,
                                          isRMDEnable: e.target.checked,
                                        };
                                      })
                                    );
                                  }}
                                />
                              </Grid>
                            </TableCell>
                            <TableCell>
                              <Grid item>
                                <Typography component={"label"}>
                                  {asset.accountType.name}
                                </Typography>
                              </Grid>
                            </TableCell>
                            <TableCell>
                              <Grid item>
                                <Typography component={"label"}>
                                  {displayCurrencyNoDecimals(
                                    asset.currentValue
                                  )}
                                </Typography>
                              </Grid>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    );
  } else if (props.needDistributionSort) {
    content = (
      <AssetDistributionSort
        assets={assets}
        update={(asset, sortValue) => {
          setAssets((v) =>
            v.map((item) => {
              if (item.editIndex !== asset.editIndex) return item;
              return {
                ...item,
                sortOrder: sortValue,
              };
            })
          );
        }}
      />
    );
  } else {
    content = (
      <React.Fragment>
        {assets.map((asset, index) => (
          <Grid item key={index} md={6}>
            <AssetCard
              asset={asset}
              fullWidth={false}
              canDelete={true}
              onDelete={handleRemoveAsset(asset)}
              handleChange={handleChange(asset)}
              clients={clients}
              contributionLimit={getContributionLimit(asset)}
            />
          </Grid>
        ))}
        <Grid item>
          <Button
            variant="outlined"
            className="add-more-assets-btn"
            onClick={handleShowAssetSelector}
          >
            + Asset Account&nbsp;
            <ChevronRightIcon />
          </Button>
          <Popover
            id={assetSelectorId}
            open={openAssetSelector}
            anchorEl={anchorEl}
            onClose={handleHideAssetSelector}
            className={"asset-class-popover"}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <Grid container md={12} style={{ padding: "40px 10px 20px 20px" }}>
              {accountTypes.map(({ key, name }) => (
                <Grid item md={4} key={key}>
                  <Button
                    variant="text"
                    className={classnames(
                      "asset-item-btn",
                      hasAsset(key) && "disabled-text"
                    )}
                    onClick={handleAddAsset(key)}
                  >
                    <AddIcon />
                    &nbsp;{name}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Popover>
        </Grid>
      </React.Fragment>
    );
  }

  return (
    <Grid container id="assets-tab" direction="row" md={9} spacing={6}>
      {content}
      {assetToDelete !== null && (
        <DeleteConfirmModal
          actionOk={() => {
            finishRemoveAsset();
          }}
          actionCancel={() => {
            setAssetToDelete(null);
          }}
        >
          <Typography component={"p"} variant={"p"}>
            Are you sure you want to delete this Asset?
          </Typography>
        </DeleteConfirmModal>
      )}
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    currentClient: state.currentClientReducer,
    currentUser: state.currentUserReducer,
  };
};

export default connect(mapStateToProps)(AssetsTab);
